import { useWs } from "./ws";
import type { ToolResultPayload, WsIncoming } from "./types";
import { envelope, isWsEnvelope } from "./ws-protocol";
import { encodeMuxJsonFrame, MUX_ANY_PEER_IDX, MuxChannel, MuxRoutingScope, muxStableStreamId } from "./mux";
import { muxPluginNumericIdForTool } from "./mux-plugin-ids";

function waitForMuxReady(ws: ReturnType<typeof useWs>, timeoutMs: number): Promise<void> {
  const isReady = () => {
    const s = ws.state;
    return s.muxReady === true && s.muxRelayRoute === true && s.muxAgentReady === true && s.muxAgentPeerIndex !== null;
  };
  if (isReady()) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    const timer = setInterval(() => {
      if (isReady()) {
        clearInterval(timer);
        resolve();
      } else if (Date.now() >= deadline) {
        clearInterval(timer);
        reject(new Error("mux_tools_not_ready"));
      }
    }, 50);
  });
}

export interface ToolCallOptions {
  timeoutMs?: number;
  userConfirmedAt?: string;
  compactOutput?:
    | boolean
    | {
        thresholdChars?: number;
        maxChars?: number;
        maxRows?: number;
      };
}

export function useToolClient() {
  const ws = useWs();
  return {
    async call<R = unknown, P extends Record<string, unknown> = Record<string, unknown>>(
      tool: string,
      params: P = {} as P,
      opts: ToolCallOptions = {},
    ): Promise<R> {
      const requestId = `ui_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      const timeoutMs = opts.timeoutMs ?? 60_000;
      const fullParams: Record<string, unknown> = opts.userConfirmedAt
        ? { ...params, userConfirmedAt: opts.userConfirmedAt }
        : { ...params };
      if (opts.compactOutput) {
        fullParams.__rwsCompactOutput = true;
        if (typeof opts.compactOutput === "object") {
          if (opts.compactOutput.thresholdChars !== undefined) {
            fullParams.__rwsCompactThresholdChars = opts.compactOutput.thresholdChars;
          }
          if (opts.compactOutput.maxChars !== undefined) fullParams.__rwsCompactMaxChars = opts.compactOutput.maxChars;
          if (opts.compactOutput.maxRows !== undefined) fullParams.__rwsCompactMaxRows = opts.compactOutput.maxRows;
        }
      }
      const request = envelope(
        "control",
        "tool",
        "request",
        { requestId, tool, params: fullParams },
        { id: requestId, deliveryClass: "must_deliver", dropPolicy: "never" },
      );
      await waitForMuxReady(ws, 10_000);
      const streamId = muxStableStreamId(requestId);

      return new Promise<R>((resolve, reject) => {
        let settled = false;
        const cleanup = () => {
          if (settled) return;
          settled = true;
          off();
          clearTimeout(timer);
        };
        const off = ws.on(
          (m: WsIncoming): boolean => {
            if (!isWsEnvelope(m, "control", "tool", "result")) return false;
            return (m as { id?: string }).id === requestId;
          },
          (m: WsIncoming) => {
            cleanup();
            const payload = ((m as { payload?: unknown }).payload ?? {}) as ToolResultPayload<R>;
            if (payload.ok) resolve(payload.result as R);
            else reject(new Error(payload.error || "tool_failed"));
          },
        );
        const timer = setTimeout(() => {
          cleanup();
          reject(new Error("tool_timeout"));
        }, timeoutMs);
        const agentPeerIndex = ws.state.muxAgentPeerIndex ?? MUX_ANY_PEER_IDX;
        ws.sendBinary(
          encodeMuxJsonFrame(
            {
              channel: MuxChannel.PluginControl,
              streamId,
              toPeerIdx: agentPeerIndex,
              pluginId: muxPluginNumericIdForTool(tool),
              routingScope: MuxRoutingScope.UnicastPeer,
            },
            request,
          ),
        );
      });
    },
  };
}
